import { useState } from 'react';
import {
  type CampoDati,
  type RigaDati,
  useArchiviaRigaDati,
  useHub,
  useMetaDati,
  useRigheDati,
  useRipristinaRigaDati,
  useSalvaRigaDati,
} from '../api/dati.js';
import { ErroreApi } from '../api/cliente.js';
import { Modale } from '../componenti/Modale.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { t } from '../i18n/lingua.js';

/**
 * L'hub dei dati generici.
 *
 * Nel v2 sono una trentina di voci di menu, ognuna con la sua maschera. Qui
 * sono un elenco a sinistra e una tabella a destra, e la tabella si costruisce
 * leggendo i campi che il servizio dichiara: non c'è una pagina per
 * anagrafica, ce n'è una sola che le sa fare tutte.
 *
 * Il vantaggio non è meno codice: è che si comportano tutte allo stesso modo.
 * Chi ha imparato ad aggiungere un tipo di pagamento sa già aggiungere un
 * vettore.
 */

export function DatiGenerici() {
  const [scelta, setScelta] = useState('payment-types');
  const [cerca, setCerca] = useState('');
  const [archiviati, setArchiviati] = useState(false);
  const [inModifica, setInModifica] = useState<RigaDati | null | undefined>(undefined);

  const hub = useHub();
  const meta = useMetaDati(scelta);
  const righe = useRigheDati(scelta, cerca, archiviati);
  const archivia = useArchiviaRigaDati(scelta);
  const ripristina = useRipristinaRigaDati(scelta);

  const voci = hub.data?.entries ?? [];
  const gruppi = [...new Set(voci.map((v) => v.group))];
  const voceScelta = voci.find((v) => v.key === scelta);
  const m = meta.data;

  const colonne: Colonna<RigaDati>[] = [
    ...(m?.fields ?? [])
      .filter((c) => c.inList)
      .map((c) => cellaPerCampo(c, m?.readOnly === false ? setInModifica : undefined)),
    ...(m && !m.readOnly
      ? [
          {
            chiave: '_azioni',
            titolo: '',
            allineamento: 'destra' as const,
            cella: (r: RigaDati) => (
              <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setInModifica(r)}>
                  {t('Modifica')}
                </button>
                {r.archived ? (
                  <button className="btn btn-ghost" onClick={() => ripristina.mutate(r.id)}>
                    {t('Ripristina')}
                  </button>
                ) : (
                  m.archivable && (
                    <button className="btn btn-ghost" onClick={() => archivia.mutate(r.id)}>
                      {t('Archivia')}
                    </button>
                  )
                )}
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Dati generici')}</h1>
          <div className="page-sub">
            {t('Le anagrafiche di supporto: quello che riempie i menu del resto del programma.')}
          </div>
        </div>
      </div>

      <div className="hub">
        <nav className="hub-nav" aria-label={t('Anagrafiche di supporto')}>
          {gruppi.map((gruppo) => (
            <div className="hub-group" key={gruppo}>
              <div className="nav-label">{gruppo}</div>
              {voci
                .filter((v) => v.group === gruppo)
                .map((v) => (
                  <button
                    key={v.key}
                    className="hub-voce"
                    aria-current={v.key === scelta ? 'page' : undefined}
                    onClick={() => {
                      setScelta(v.key);
                      setCerca('');
                      setArchiviati(false);
                    }}
                  >
                    <span>{v.label}</span>
                    <span className="hub-conta">{v.count.toLocaleString('it-IT')}</span>
                  </button>
                ))}
            </div>
          ))}
        </nav>

        <div className="hub-corpo">
          <div className="card">
            <div className="card-head">
              <span className="card-title">{m?.label ?? voceScelta?.label ?? '…'}</span>
              {m?.readOnly && <span className="badge">{t('sola lettura')}</span>}
              <span style={{ marginLeft: 'auto' }} />
              <input
                className="input"
                style={{ height: 26, width: 190 }}
                placeholder={t('Cerca')}
                value={cerca}
                onChange={(e) => setCerca(e.target.value)}
              />
              {m?.archivable && (
                <button
                  className={archiviati ? 'chip chip-set' : 'chip'}
                  onClick={() => setArchiviati((v) => !v)}
                >
                  {archiviati ? t('Con gli archiviati') : 'Mostra archiviati'}
                </button>
              )}
              {m && !m.readOnly && (
                <button className="btn btn-primary" onClick={() => setInModifica(null)}>
                  {t('Nuovo')}
                </button>
              )}
            </div>

            {m && (
              <div className="hub-nota">
                {m.description}
                {m.readOnly && m.readOnlyReason && (
                  <>
                    {' '}
                    <strong>{m.readOnlyReason}</strong>
                  </>
                )}
                {voceScelta?.perCompany && ' Vale solo per l’azienda con cui stai lavorando.'}
              </div>
            )}

            <Tabella
              colonne={colonne}
              righe={righe.data?.rows ?? []}
              chiaveRiga={(r) => r.id}
              caricamento={righe.isFetching}
              errore={righe.error}
              vuoto={cerca ? t('Nessun risultato per «{cerca}».', { cerca }) : t('Non c’è ancora niente qui.')}
            />
          </div>
        </div>
      </div>

      {inModifica !== undefined && m && (
        <ModaleRigaDati
          chiave={scelta}
          meta={m}
          riga={inModifica}
          onChiudi={() => setInModifica(undefined)}
        />
      )}
    </>
  );
}

/** Come si disegna una cella, a partire dal tipo dichiarato dal servizio. */
function cellaPerCampo(
  campo: CampoDati,
  apri: ((riga: RigaDati) => void) | undefined,
): Colonna<RigaDati> {
  return {
    chiave: campo.key,
    titolo: campo.label,
    monospazio: campo.mono,
    cella: (r) => {
      const valore = r[campo.key];
      if (campo.type === 'siNo') {
        return valore ? <span className="badge badge-ok">{t('sì')}</span> : <span className="dim">{t('no')}</span>;
      }
      if (valore === null || valore === undefined || valore === '') {
        return <span className="dim">—</span>;
      }
      if (campo.type === 'scelta') {
        const scelta = campo.values?.find((v) => v.value === String(valore));
        return scelta?.label ?? String(valore);
      }
      const testo = String(valore);
      // il primo campo è il nome della riga: da lì si apre la modifica
      return apri && campo.required ? (
        <button className="cell-link strong" onClick={() => apri(r)}>
          {testo}
          {r.archived ? <span className="badge" style={{ marginLeft: 8 }}>{t('archiviata')}</span> : null}
        </button>
      ) : (
        <>
          {testo}
          {campo.required && r.archived ? (
            <span className="badge" style={{ marginLeft: 8 }}>
              {t('archiviata')}
            </span>
          ) : null}
        </>
      );
    },
  };
}

/**
 * Il modulo di modifica: anche questo si costruisce dai campi dichiarati.
 * Un campo nuovo nel registro compare qui senza toccare niente.
 */
function ModaleRigaDati({
  chiave,
  meta,
  riga,
  onChiudi,
}: {
  chiave: string;
  meta: ReturnType<typeof useMetaDati>['data'] & object;
  riga: RigaDati | null;
  onChiudi: () => void;
}) {
  const [valori, setValori] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      meta.fields.map((c) => [c.key, riga?.[c.key] ?? (c.type === 'siNo' ? false : '')]),
    ),
  );
  const salva = useSalvaRigaDati(chiave);

  const cambia = (chiaveCampo: string, valore: unknown) =>
    setValori((v) => ({ ...v, [chiaveCampo]: valore }));

  const conferma = () => {
    const corpo: Record<string, unknown> = {};
    for (const c of meta.fields) {
      const valore = valori[c.key];
      if (c.type === 'numero' || c.type === 'decimale') {
        corpo[c.key] = valore === '' || valore === null ? null : Number(valore);
      } else {
        corpo[c.key] = valore;
      }
    }
    salva.mutate({ ...corpo, ...(riga ? { id: riga.id } : {}) }, { onSuccess: onChiudi });
  };

  const errore = salva.error instanceof ErroreApi ? salva.error.message : null;

  return (
    <Modale
      titolo={riga ? t('Modifica · {nome}', { nome: meta.label }) : t('Nuovo · {nome}', { nome: meta.label })}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi}>
            {t('Annulla')}
          </button>
          <button className="btn btn-primary" onClick={conferma} disabled={salva.isPending}>
            {salva.isPending ? 'Salvo…' : 'Salva'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}
      <div className="fields">
        {meta.fields.map((c) => (
          <div
            key={c.key}
            className={c.type === 'testoLungo' ? 'field span-2' : 'field'}
          >
            <label htmlFor={`dg-${c.key}`}>
              {c.label}
              {c.required && <span className="req">*</span>}
            </label>
            {c.type === 'siNo' ? (
              <span className="switch-row">
                <button
                  id={`dg-${c.key}`}
                  className="switch"
                  role="switch"
                  type="button"
                  aria-checked={valori[c.key] === true}
                  aria-label={c.label}
                  onClick={() => cambia(c.key, !valori[c.key])}
                />
                <span className="dim">{valori[c.key] ? 'sì' : 'no'}</span>
              </span>
            ) : c.type === 'scelta' ? (
              <select
                id={`dg-${c.key}`}
                className="select"
                value={String(valori[c.key] ?? '')}
                onChange={(e) => cambia(c.key, e.target.value)}
              >
                <option value="">{t('Scegli…')}</option>
                {c.values?.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            ) : c.type === 'testoLungo' ? (
              <textarea
                id={`dg-${c.key}`}
                className="textarea"
                rows={3}
                value={String(valori[c.key] ?? '')}
                onChange={(e) => cambia(c.key, e.target.value)}
              />
            ) : (
              <input
                id={`dg-${c.key}`}
                className={c.mono ? 'input num' : 'input'}
                type={c.type === 'numero' || c.type === 'decimale' ? 'number' : 'text'}
                step={c.type === 'decimale' ? '0.01' : undefined}
                value={String(valori[c.key] ?? '')}
                onChange={(e) => cambia(c.key, e.target.value)}
              />
            )}
            {c.help && (
              <span className="dim" style={{ fontSize: 11 }}>
                {c.help}
              </span>
            )}
          </div>
        ))}
      </div>
    </Modale>
  );
}
